import { z } from 'zod';

import type {
  WorkflowDetail,
  WorkflowParameter,
  WorkflowStep,
  WorkflowStepDetailSection,
} from '@/features/rpa/recorder/types/workflow';

/**
 * Mirrors ml-scribe `domains/rpa/recording/schemas.py` (`WorkflowDataResponse`,
 * `WorkflowStepResponse`, `WorkflowParameterResponse`).
 */
export const workflowParameterResponseSchema = z.object({
  name: z.string(),
  type: z.string(),
  description: z.string(),
  example: z.string(),
  required: z.boolean(),
  default_value: z.string().nullable().optional(),
});

export const workflowStepResponseSchema = z.object({
  step_number: z.number().int(),
  action_description: z.string(),
  step_type: z.string(),
  target_visual_description: z.string(),
  target_location: z.string(),
  nearby_landmarks: z.string(),
  details: z.string(),
  expected_result: z.string(),
  wait_after_seconds: z.number().optional().default(0),
  parameter_refs: z.array(z.string()).optional().default([]),
  /** Not in the public API model today; kept for forward-compatible stored JSON. */
  skill_id: z.string().optional(),
  frame_ref: z.string().optional(),
  frame_url: z.string().optional(),
  frame_data_url: z.string().optional(),
});

export const workflowDataResponseSchema = z.object({
  workflow_name: z.string(),
  workflow_description: z.string(),
  prerequisites: z.array(z.string()).optional().default([]),
  parameters: z.array(workflowParameterResponseSchema).optional().default([]),
  steps: z.array(workflowStepResponseSchema).optional().default([]),
  notes: z.array(z.string()).optional().default([]),
});

export type WorkflowDataResponse = z.infer<typeof workflowDataResponseSchema>;
export type WorkflowStepResponse = z.infer<typeof workflowStepResponseSchema>;
export type WorkflowParameterResponse = z.infer<typeof workflowParameterResponseSchema>;

export const workflowDetailSchema = workflowDataResponseSchema.transform(
  (data): WorkflowDetail => ({
    name: data.workflow_name,
    description: nonEmpty(data.workflow_description),
    prerequisites: data.prerequisites.filter((item) => item.trim().length > 0),
    notes: data.notes.filter((item) => item.trim().length > 0),
    parameters: data.parameters.map(mapParameter),
    steps: data.steps.map(mapStep),
  })
);

function mapParameter(param: WorkflowParameterResponse): WorkflowParameter {
  return {
    name: param.name,
    required: param.required,
    type: param.type,
    description: param.description,
    example: param.example,
    ...(param.default_value != null && param.default_value !== ''
      ? { defaultValue: param.default_value }
      : {}),
  };
}

function mapStep(step: WorkflowStepResponse, index: number): WorkflowStep {
  const stepNumber = step.step_number > 0 ? step.step_number : index + 1;

  return {
    id: `step-${stepNumber}`,
    stepNumber,
    label: stripTrailingFullStop(step.action_description),
    detailSections: buildDetailSections(step),
    ...(step.skill_id ? { skillId: step.skill_id } : {}),
    frameRef: step.frame_ref,
    frameUrl: step.frame_url,
    frameDataUrl: step.frame_data_url,
  };
}

function buildDetailSections(step: WorkflowStepResponse): WorkflowStepDetailSection[] {
  const sections: WorkflowStepDetailSection[] = [];
  const primaryDetail =
    nonEmpty(step.details) ?? nonEmpty(step.target_visual_description);

  if (primaryDetail) sections.push({ kind: 'details', value: primaryDetail });
  if (nonEmpty(step.target_location)) {
    sections.push({ kind: 'location', value: step.target_location });
  }
  // `expected_result` is no longer suppressed when a location exists — a
  // clinician reviewing the step wants both "where" and "what should happen".
  if (nonEmpty(step.expected_result)) {
    sections.push({ kind: 'expected', value: step.expected_result });
  }
  if (sections.length === 0 && nonEmpty(step.step_type)) {
    sections.push({ kind: 'action', value: step.step_type });
  }
  return sections;
}

function stripTrailingFullStop(value: string): string {
  return value.endsWith('.') ? value.slice(0, -1) : value;
}

function nonEmpty(value: string): string | undefined {
  return value.trim().length > 0 ? value : undefined;
}
